import { Checkbox, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import React, { useEffect, useMemo, useState } from "react";
import { DigitalWaiver, Experience } from "types";
import { SelectionState, useSelection } from "../../../hooks/useSelection";
import { NoTranslate } from "../../Common/NoTranslate";
import { ProductImage } from "../../Common/ProductImage";
import { SearchInput } from "../../Common/SearchInput";

interface DigitalWaiversExperiencesSelectorProps {
    value: SelectionState;
    onChange: (value: SelectionState) => void;
    classNames?: { list?: string; checkbox?: string };
    waiverTemplates: DigitalWaiver[];
    waiverTemplate: DigitalWaiver;
    experiences: Experience[];
}

const defaultValue: SelectionState = {
    all: true,
    selected: [],
};

export const DigitalWaiversExperiencesSelector = ({
    value = defaultValue,
    onChange,
    classNames,
    waiverTemplates,
    waiverTemplate,
    experiences,
}: DigitalWaiversExperiencesSelectorProps) => {
    const [searchTerm, setSearchTerm] = useState("");

    const filteredExperiences = searchTerm
        ? experiences?.filter((exp) => exp.name.toLowerCase().includes(searchTerm.trim().toLowerCase()))
        : experiences;

    const isFiltered = searchTerm.length > 0;
    const defaultSelection = useMemo(() => ({ all: value.all, selected: value.selected }), [value]);
    const selection = useSelection(filteredExperiences, isFiltered, defaultSelection);

    useEffect(() => {
        onChange({ all: selection.state.all, selected: selection.state.selected });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selection.state]);

    const handleSearchChange = (searchResult: string) => {
        setSearchTerm(searchResult.trim());
    };

    const isDisabled = (exp: Experience): boolean => {
        if (exp?.waiverPreference?.remoteId) {
            const associatedWaverTemplate = waiverTemplates.find((waiverTemplate: DigitalWaiver) => {
                return waiverTemplate.id === exp?.waiverPreference?.remoteId;
            });
            return !!(associatedWaverTemplate && waiverTemplate.id !== associatedWaverTemplate.id);
        }

        return false;
    };

    const getTooltipContent = (exp: Experience) => {
        const hasTooltipContent = isDisabled(exp);
        if (hasTooltipContent && exp?.waiverPreference?.remoteId) {
            const associatedWaverTemplate = waiverTemplates.find(
                (waiverTemplate: DigitalWaiver) => waiverTemplate.id === exp?.waiverPreference?.remoteId,
            );
            if (associatedWaverTemplate && waiverTemplate.id !== associatedWaverTemplate.id) {
                return (
                    <>
                        <var data-var="experienceName">{exp.name}</var> is already associated with
                        <var data-var="associatedWavierTemplateName">{associatedWaverTemplate.name}</var>
                    </>
                );
            }
        }

        return "";
    };

    return (
        <div className="ui-product-selector overflow-hidden rounded-lg border border-gray-lighter">
            <div className="space-y-3 p-3">
                <SearchInput className="w-full" onChange={handleSearchChange} />

                <div
                    className={clsx(
                        "h-60 divide-y divide-gray-light overflow-y-auto overflow-x-hidden rounded border border-gray-light",
                        classNames?.list,
                    )}
                >
                    {filteredExperiences?.map((exp: Experience) => {
                        if (isDisabled(exp)) {
                            return (
                                <Tooltip key={exp.id} content={getTooltipContent(exp)}>
                                    <Checkbox
                                        key={exp.id}
                                        checked={selection.isSelected(exp)}
                                        disabled={isDisabled(exp)}
                                        name={exp.id}
                                        label={<DigitalWaiverCheckboxContent experience={exp} />}
                                        className={clsx(
                                            "px-3 py-2 font-bold",
                                            selection.isSelected(exp) && "bg-primary-lighter",
                                            !isDisabled(exp) && "cursor-pointer hover:bg-gray-lighter",
                                        )}
                                        classNames={{ checkbox: clsx("!border !border-gray", classNames?.checkbox) }}
                                        onChange={() => selection.select(exp)}
                                    />
                                </Tooltip>
                            );
                        }

                        return (
                            <Checkbox
                                key={exp.id}
                                checked={selection.isSelected(exp)}
                                disabled={isDisabled(exp)}
                                name={exp.id}
                                label={<DigitalWaiverCheckboxContent experience={exp} />}
                                className={clsx(
                                    "px-3 py-2 font-bold",
                                    selection.isSelected(exp) && "bg-primary-lighter",
                                    !isDisabled(exp) && "cursor-pointer hover:bg-gray-lighter",
                                )}
                                classNames={{ checkbox: clsx("!border !border-gray", classNames?.checkbox) }}
                                onChange={() => selection.select(exp)}
                            />
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

const DigitalWaiverCheckboxContent = ({ experience }: { experience: Experience }) => {
    return (
        <span className="flex items-center">
            <ProductImage product={experience} size="tiny" />
            <NoTranslate className="ml-2 font-bold text-black">{experience.name.trim()}</NoTranslate>
        </span>
    );
};
